import type {AnatomyPart} from '@zag-js/anatomy';
import React, {cloneElement, type ReactElement} from 'react';

const warning = console.warn;

// slot config allows 2 options:
// 1. Component to match, example: { leadingVisual: LeadingVisual }
type ComponentMatcher = React.ElementType;
// 2. Component to match + a test function (using props), example: { blockDescription: [Description, props => props.variant === 'block'] }
// 3. Component to match + a test function (using component type), example: { blockDescription: [Description, (_props, type) => type === Description] }
type ComponentAndPropsMatcher = [ComponentMatcher, (props: any, type?: ReactElement<unknown> | string) => boolean];

export type SlotConfig = Record<string, ComponentMatcher | ComponentAndPropsMatcher>;

type SlotElements<Config extends SlotConfig> = {
  [Property in keyof Config]: SlotValue<Config, Property>;
};

type SlotValue<Config, Property extends keyof Config> = Config[Property] extends React.ElementType // config option 1
  ? React.ReactElement<React.ComponentPropsWithoutRef<Config[Property]>, Config[Property]>
  : Config[Property] extends readonly [
        infer ElementType extends React.ElementType, // config option 2, infer array[0] as component
        infer _TestFn extends (props: any, type?: ReactElement<unknown> | string) => boolean,
      ]
    ? React.ReactElement<React.ComponentPropsWithoutRef<ElementType>, ElementType>
    : never; // useful for narrowing types, third option is not possible

/**
 * Extract components from `children` so we can render them in different places,
 * allowing us to implement components with SSR-compatible slot APIs.
 * Note: We can only extract direct children, not nested ones.
 * @example
 *     const [slots, childrenWithoutSlots] = useSlots<SlotsConfig, WidgetContext>(
      children,
      {
        header: WidgetHeader,
        body: WidgetBody,
        footer: WidgetFooter,
      },
      {isInteractive, isEmpty, isLoading},
    );

    return (
      <ChakraDiv>
        {slots.header}
        {slots.body}
        {childrenWithoutSlots}
        {slots.footer}
      </ChakraDiv>
 */
export const useSlots = <Config extends SlotConfig, TContext extends Record<string, unknown>>(
  children: React.ReactNode,
  config: Config,
  parts: Record<keyof Config, AnatomyPart>,
  context: TContext,
): [Partial<SlotElements<Config>>, Array<React.ReactNode>] => {
  // Object mapping slot names to their elements
  const slots: Partial<SlotElements<Config>> = mapValues(config, () => undefined);

  // Array of elements that are not slots
  const rest: Array<React.ReactNode> = [];

  const keys = Object.keys(config) as Array<keyof Config>;
  const values = Object.values(config);

  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) {
      rest.push(child);
      return;
    }

    const index = values.findIndex(value => {
      if (Array.isArray(value)) {
        const [component, testFn] = value;
        return child.type === component && testFn(child.props, child);
      } else {
        return child.type === value;
      }
    });

    // If the child is not a slot, add it to the `rest` array
    if (index === -1) {
      rest.push(child);
      return;
    }

    const slotKey = keys[index];

    // If slot is already filled, ignore duplicates
    if (slots[slotKey]) {
      warning(true, `Found duplicate "${String(slotKey)}" slot. Only the first will be rendered.`);
      return;
    }

    // If the child is a slot, add it to the `slots` object
    // Inject the context if it exists
    if (context) {
      slots[slotKey] = cloneElement(child, {
        ...(child as any).props,
        ...(parts[slotKey].attrs ?? {}),
        ...context,
      }) as SlotValue<Config, keyof Config>;
    } else {
      slots[slotKey] = cloneElement(child, {...(child as any).props, ...(parts[slotKey]?.attrs ?? {})}) as SlotValue<
        Config,
        keyof Config
      >;
    }
  });

  return [slots, rest];
};

/** Map the values of an object */
const mapValues = <T extends Record<string, unknown>, V>(obj: T, fn: (value: T[keyof T]) => V) => {
  return Object.keys(obj).reduce(
    (result, key: keyof T) => {
      result[key] = fn(obj[key]);
      return result;
    },
    {} as Record<keyof T, V>,
  );
};
